import React, { useState } from 'react'
import { useMutation } from '@apollo/client'
import Modal from 'react-bootstrap/Modal'
import {
    Button,
    Dropdown,
    Form,
    Message,
} from 'semantic-ui-react'
import {
    SEND_AUTH_EMAIL_TO_USER,
    UPDATE_USER_AUTH,
} from '../../../graphql/admin'
import { AUTH_EMAIL_TYPES, TARGET_APP_OPTIONS } from './helpers'

const AuthUserActions = ({ record, onCompleted }) => {
    const user = record.user
    const [error, setError] = useState('')
    const [emailModalOpen, setEmailModalOpen] = useState(false)
    const [blockModalOpen, setBlockModalOpen] = useState(false)
    const [sendEmailModalOpen, setSendEmailModalOpen] = useState(false)
    const [newEmail, setNewEmail] = useState(user.email || '')
    const [blockedReason, setBlockedReason] = useState(record.blockedReason || '')
    const [authEmailType, setAuthEmailType] = useState('password_reset')
    const [targetApp, setTargetApp] = useState('expert')

    const [updateUserAuth, { loading: updating }] = useMutation(UPDATE_USER_AUTH)
    const [sendAuthEmailToUser, { loading: sending }] = useMutation(
        SEND_AUTH_EMAIL_TO_USER
    )

    const runAction = async (action) => {
        setError('')
        try {
            await action()
            await onCompleted()
        } catch (err) {
            setError(err.message)
        }
    }

    const updateAuth = (input) =>
        runAction(() =>
            updateUserAuth({
                variables: {
                    userId: user.id,
                    input,
                },
            })
        )

    const onUpdateEmail = async () => {
        await updateAuth({ email: newEmail.trim() })
        setEmailModalOpen(false)
    }

    const onBlock = async () => {
        await updateAuth({
            isBlocked: true,
            blockedReason: blockedReason.trim() || null,
        })
        setBlockModalOpen(false)
    }

    const onSendAuthEmail = async () => {
        await runAction(() =>
            sendAuthEmailToUser({
                variables: {
                    userId: user.id,
                    type: authEmailType,
                    targetApp,
                },
            })
        )
        setSendEmailModalOpen(false)
    }

    return (
        <div className="auth-user-actions">
            <Dropdown
                button
                primary
                text="Actions"
                loading={updating || sending}
                disabled={updating || sending}
            >
                <Dropdown.Menu>
                    <Dropdown.Item
                        icon="mail"
                        text="Send auth email"
                        onClick={() => setSendEmailModalOpen(true)}
                    />
                    <Dropdown.Item
                        icon="edit"
                        text="Change email"
                        onClick={() => {
                            setNewEmail(user.email || '')
                            setEmailModalOpen(true)
                        }}
                    />
                    <Dropdown.Item
                        icon={record.emailVerified ? 'remove circle' : 'check circle'}
                        text={
                            record.emailVerified
                                ? 'Mark email unverified'
                                : 'Mark email verified'
                        }
                        onClick={() =>
                            updateAuth({ emailVerified: !record.emailVerified })
                        }
                    />
                    <Dropdown.Divider />
                    {record.isBlocked ? (
                        <Dropdown.Item
                            icon="unlock"
                            text="Unblock user"
                            onClick={() => updateAuth({ isBlocked: false })}
                        />
                    ) : (
                        <Dropdown.Item
                            icon="ban"
                            text="Block user"
                            onClick={() => setBlockModalOpen(true)}
                        />
                    )}
                </Dropdown.Menu>
            </Dropdown>
            {error && (
                <Message
                    negative
                    size="small"
                    className="auth-user-action-message"
                    content={error}
                />
            )}

            <Modal
                show={emailModalOpen}
                onHide={() => setEmailModalOpen(false)}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Change Email</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Input
                            label="Email"
                            type="email"
                            value={newEmail}
                            onChange={(e, { value }) => setNewEmail(value)}
                        />
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={() => setEmailModalOpen(false)}>
                        Cancel
                    </Button>
                    <Button primary loading={updating} onClick={onUpdateEmail}>
                        Save email
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal
                show={blockModalOpen}
                onHide={() => setBlockModalOpen(false)}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Block User</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.TextArea
                            label="Reason"
                            value={blockedReason}
                            onChange={(e, { value }) => setBlockedReason(value)}
                            placeholder="Briefly describe why this account is being blocked."
                        />
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={() => setBlockModalOpen(false)}>
                        Cancel
                    </Button>
                    <Button negative loading={updating} onClick={onBlock}>
                        Block user
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal
                show={sendEmailModalOpen}
                onHide={() => setSendEmailModalOpen(false)}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Send Auth Email</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Select
                            label="Email type"
                            options={AUTH_EMAIL_TYPES}
                            value={authEmailType}
                            onChange={(e, { value }) => setAuthEmailType(value)}
                        />
                        <Form.Select
                            label="Target app"
                            options={TARGET_APP_OPTIONS}
                            value={targetApp}
                            onChange={(e, { value }) => setTargetApp(value)}
                        />
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={() => setSendEmailModalOpen(false)}>
                        Cancel
                    </Button>
                    <Button primary loading={sending} onClick={onSendAuthEmail}>
                        Send email
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    )
}

export default AuthUserActions
